import styled, { css, DefaultTheme } from 'styled-components';
import { Icon } from '../../icon';
import { memoizeFunction } from '@agilepoint/utils-common';
import { CheckboxState } from '../theme';

interface ICustomCheckboxProps {
    $checked?: boolean | 'intermediate';
    $disabled?: boolean;
    $error?: boolean;
    $focus?: boolean;
}

const centeredPositionStyle = css`
    position: absolute;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
`;

const getCheckboxStylesByState = memoizeFunction((state: CheckboxState) => {
    const getStyles = (theme: DefaultTheme) => {
        const { checkbox: { colors } } = theme;
        return colors[state];
    };
    return css`
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
    `;
});

const getCheckMarkColorByState = memoizeFunction(({ $checked, $error, $disabled, theme }: ICustomCheckboxProps & { theme: DefaultTheme }) => {
    const { checkbox: { colors } } = theme;
    let state: CheckboxState = $checked ? 'checked' : 'default';
    if ($disabled) {
        state = $checked ? 'disabledAndChecked' : 'disabled';
    }
    if ($error) {
        state = $checked ? 'errorAndChecked' : 'error';
    }
    return colors[state].checkMarkColor;
});

export const CustomCheckbox = styled.span.attrs(() => ({
    className: String('ap-checkbox')
})) <ICustomCheckboxProps>`
    position: relative;
    display: inline-block;
    border-width: 1px;
    border-style: solid;
    box-sizing: border-box;
    vertical-align: middle;
    height: 18px;
    width: 18px;
    border-radius: ${({ theme: { checkbox: { composites } } }) => composites.borderRadius};
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

    ${({ $checked, $error, $disabled }) => {
        if ($disabled) {
            return getCheckboxStylesByState($checked ? 'disabledAndChecked' : 'disabled');
        }

        if ($error) {
            return getCheckboxStylesByState($checked ? 'errorAndChecked' : 'error');
        }

        return getCheckboxStylesByState($checked ? 'checked' : 'default');
    }};

    &:hover {
        ${({ $disabled, $checked }) => getCheckboxStylesByState($disabled ? ($checked ? 'disabledAndChecked' : 'disabled') : ($checked ? 'checked' : 'hovered'))};
        opacity: ${({ $disabled, $checked }) => (($disabled || !!$checked) ? 1 : 0.5)};
    }

    :focus {
        box-shadow: ${({ $disabled, theme: { semantics: { palette } } }) => ($disabled ? 'none' : `0px 0px 4px ${palette.themePrimary}`)};
        outline: initial;
        border-color: ${({ $disabled, theme: { semantics: { palette } } }) => ($disabled ? 'inherit' : `${palette.themePrimary}`)};
    }
`;

export const CheckboxCheckedIcon = styled(Icon)`
    ${centeredPositionStyle};
    fill: ${getCheckMarkColorByState};
`;

export const ChechkboxIntermediate = styled.span`
    ${centeredPositionStyle};
    background-color: ${getCheckMarkColorByState};
    height: 2px;
    width: 10px;
`;
